// Libs
import classNames from 'classnames/bind';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  InputLabel,
  Stack,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import { TFunction } from 'i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
// Components, Layouts, Pages
import {
  BaseFilters,
  BaseInputSearch,
  BaseSelect,
  BaseSplitButton,
  BaseStatus,
  BaseTable,
  BaseTableOption,
  BaseTableUser,
  BaseToolbar,
  ConfirmModal,
  Loading,
  UnderDevelopment,
} from '~/components/common';
import { EmployeeDetailModal } from '~/components/module/employee';
import PageShell from '~/pages/common/pageShell/PageShell';
// Others
import useDebounce from '~/utils/hooks/useDebounce';
import { useAppDispatch } from '~/utils/redux/hooks';
import {
  deleteEmployee,
  getEmployeeList,
  updateStatusEmployee,
  uploadFileExcel,
} from '~/thunks/employee/employeeThunk';
import { employeeActions, selectIsRefreshEmployeeList } from '~/thunks/employee/employeeSlice';
import { IPaginationResponse } from '~/utils/interface/base';
import { IBaseOption, IFilterValueChange, IHeaderCellType, IQueryParamBase } from '~/utils/interface/common';
import { IEmployee, IQueryDeleteEmployee } from '~/utils/interface/employee';
import {
  BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
  DEFAULT_DEBOUNCE_DELAY_TIME,
  DEFAULT_LIMIT_PAGE,
  DEFAULT_NUMBER_ZERO,
  EMPTY_STRING,
  FILTER_DEFAULT_VALUES,
  SEARCH_BY_KEY,
} from '~/utils/constants/common';
import {
  AcceptFileEnum,
  EmployeeStatusEnum,
  RoleEnum,
  SearchByFilterContentEnum,
  SearchByFilterEnum,
  SortEnum,
  ToastTypeEnum,
} from '~/utils/enum/common';
import { convertCodeToRole, customToast, formatPhoneNumber, formatTitleCase } from '~/utils/helper/common';
import { adminRouteAbsolute } from '~/utils/routes/admin';
import { staffRouteAbsolute } from '~/utils/routes/staff';
import { StorageEnum } from '~/utils/enum/base';
import { mockDataEmployeeStatus } from '~/utils/mockData/employee';
import { createNewConversation } from '~/thunks/conversation/conversationThunk';
import { IQueryCreateNewConversation } from '~/utils/interface/conversation';
// Styles, images, icons
import styles from './Employee.module.scss';
import { icons } from '~/assets';

type Props = {};

const cx = classNames.bind(styles);

const columns = (
  t: TFunction,
  onView: (id: string) => void,
  onEdit: (id: string) => void,
  onDelete: (id: string) => void,
  onMakeStatus: (employee: IEmployee) => void,
  onMessage: (employeeId: string) => void
): IHeaderCellType<IEmployee>[] => {
  return [
    {
      id: 'name',
      label: t('table_head_title_name'),
      minWidth: 200,
      render: ({ avatarImage, firstName, lastName, ...rest }: IEmployee) => {
        return <BaseTableUser user={{ avatar: avatarImage, firstName, lastName }} />;
      },
    },
    {
      id: 'email',
      label: t('table_head_title_email'),
      minWidth: 300,
      render: (record: IEmployee) => record?.email || EMPTY_STRING,
    },
    {
      id: 'phone',
      label: t('table_head_title_phone'),
      minWidth: 150,
      render: (record: IEmployee) => {
        return formatPhoneNumber(record?.phoneNumber);
      },
    },
    {
      id: 'role',
      label: t('table_head_title_position'),
      render: (record: IEmployee) => (
        <BaseStatus label={t(convertCodeToRole(record?.role), { ns: 'common/base' })} />
      ),
    },
    {
      id: 'status',
      label: t('table_head_title_status'),
      render: (record: IEmployee) => <BaseStatus label={record?.status} />,
    },
    {
      id: 'action',
      label: t('table_head_title_action'),
      align: 'left',
      render: (record: IEmployee) => {
        return (
          <BaseTableOption
            onView={() => onView(record.id)}
            onEdit={() => onEdit(record.id)}
            onDelete={() => onDelete(record.id)}
            onMakeStatus={() => onMakeStatus(record)}
            onMessage={() => onMessage(record.id)}
          />
        );
      },
    },
  ];
};

const Employee = (props: Props) => {
  //#region Destructuring Props

  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/employee']);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const fileEmployeeRef = useRef<HTMLInputElement | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const params = useMemo(() => Object.fromEntries([...searchParams]), [searchParams]);
  const pageSelected = useMemo<number>(
    () => Number(params?.page ?? BASE_PAGINATION_DEFAULT_CURRENT_PAGE),
    [params?.page]
  );

  const limitPage = useMemo<number>(() => Number(params?.limit ?? DEFAULT_LIMIT_PAGE), [params?.limit]);
  const textSearchParams = useMemo<string>(
    () => String(params?.textSearch || EMPTY_STRING),
    [params?.textSearch]
  );
  const currentRole: string = localStorage.getItem(StorageEnum.ROLE) ?? EMPTY_STRING;
  //#endregion Declare Hook

  //#region Selector
  const isRefreshEmployeeList = useSelector(selectIsRefreshEmployeeList);
  //#endregion Selector

  //#region Declare State
  const [isShowUnderDevelopment, setIsShowUnderDevelopment] = useState<boolean>(false);
  const [filterParams, setFilterParams] = useState<IQueryParamBase>(FILTER_DEFAULT_VALUES);
  const [paramObject, setParamObject] = useState<IQueryParamBase>({
    page: pageSelected,
    limit: limitPage,
  });
  const [searchPlaceholder, setSearchPlaceholder] = useState<string>(EMPTY_STRING);
  const [employeeList, setEmployeeList] = useState<IEmployee[]>([]);
  const [pagination, setPagination] = useState<IPaginationResponse>();
  const [searchKey, setSearchKey] = useState<string>(EMPTY_STRING);
  const debounceSearchKey = useDebounce(searchKey, DEFAULT_DEBOUNCE_DELAY_TIME);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  // const [idsEmployee, setIdsEmployee] = useState<string[]>([]);
  const [totalItems, setTotalItems] = useState<string>(EMPTY_STRING);

  const [isOpenEmployeeDetail, setIsOpenEmployeeDetail] = useState<boolean>(false);
  const [employeeSelected, setEmployeeSelected] = useState<string>();
  const [isOpenConfirm, setIsOpenConfirm] = useState<boolean>(false);
  const [dataIds, setDataIds] = useState<string[]>([]);

  const [isShowConfirmMakeStatus, setIsShowConfirmMakeStatus] = useState<boolean>(false);
  const [employee, setEmployee] = useState<IEmployee | undefined>();

  const [employeeFileSelected, setEmployeeFileSelected] = useState<File | null>(null);
  //#endregion Declare State

  //#region Implement Hook
  useEffect(() => {
    const newParamsObject = { ...paramObject, sortBy: SearchByFilterEnum.STATUS, sortType: SortEnum.ASC };

    const { status, ...rest } = newParamsObject;

    const params = paramObject?.status === EmployeeStatusEnum.ALL ? rest : newParamsObject;

    handleGetEmployeeList(params);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramObject]);

  useEffect(() => {
    if (!isRefreshEmployeeList) return;

    const newParamsObject = { ...paramObject, sortBy: SearchByFilterEnum.STATUS, sortType: SortEnum.ASC };

    const { status, ...rest } = newParamsObject;

    const params = paramObject?.status === EmployeeStatusEnum.ALL ? rest : newParamsObject;

    handleGetEmployeeList(params);
    dispatch(employeeActions.setRefreshEmployeeList(false));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRefreshEmployeeList]);

  useEffect(() => {
    setSearchKey(textSearchParams);
  }, [textSearchParams]);

  useEffect(() => {
    if (pagination || debounceSearchKey) {
      setParamObject({
        ...paramObject,
        page: BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
        limit: limitPage,
        ...(debounceSearchKey ? { textSearch: debounceSearchKey } : { textSearch: undefined }),
        ...(filterParams ? { ...filterParams } : {}),
        sortBy: SearchByFilterEnum.STATUS,
        sortType: SortEnum.ASC,
      });

      setSearchParams({
        page: String(BASE_PAGINATION_DEFAULT_CURRENT_PAGE),
        limit: String(limitPage),
        ...(searchKey ? { textSearch: debounceSearchKey } : {}),
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounceSearchKey]);

  useEffect(() => {
    if (Array(filterParams.fields)) {
      const placeholder = filterParams.fields
        ?.map((item) => {
          if (item === SearchByFilterEnum.ROLE) {
            return formatTitleCase(SearchByFilterEnum.POSITION);
          }

          return formatTitleCase(item);
        })
        ?.join(', ');

      setSearchPlaceholder(placeholder as string);
    }
  }, [filterParams.fields]);

  useEffect(() => {
    if (!searchKey) return;

    setParamObject({
      ...paramObject,
      page: BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
      limit: limitPage,
      ...(filterParams ? { ...filterParams } : {}),
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterParams]);

  useEffect(() => {
    if (!employeeFileSelected) return;

    handleUploadFile(employeeFileSelected);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeFileSelected]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleApplyFilter = (values: IQueryParamBase | undefined) => {
    if (!values) {
      setFilterParams({});
      return;
    }

    setFilterParams({
      ...values,
    });

    if (values?.status) {
      setParamObject({
        ...paramObject,
        status: values?.status,
      });
    }
  };

  const handleNavigateToFormEmployee = () => {
    switch (currentRole) {
      case RoleEnum.ADMIN:
        navigate(adminRouteAbsolute.createEmployee);
        break;

      case RoleEnum.STAFF:
        navigate(staffRouteAbsolute.createEmployee);
        break;
    }
  };

  const handleUploadFile = async (file: File) => {
    setIsLoading(true);
    const formData = new FormData();
    formData.append('file', file);

    dispatch(uploadFileExcel(formData))
      .unwrap()
      .then((res) => {
        dispatch(employeeActions.setRefreshEmployeeList(true));
      })
      .catch((error) => {})
      .finally(() => {
        setIsLoading(false);
        setEmployeeFileSelected(null);
        if (fileEmployeeRef.current) {
          fileEmployeeRef.current.value = EMPTY_STRING;
        }
      });
  };

  const onFilterChildrenChange = (
    event: ChangeEvent<HTMLInputElement>,
    valueFilter: IQueryParamBase | undefined,
    onChange?: (value: IFilterValueChange<IQueryParamBase>) => void
  ) => {
    const { name, value, checked } = event.target;
    const newFields: IQueryParamBase = {
      ...filterParams,
      fields: checked
        ? [...(valueFilter?.fields || []), value]
        : (valueFilter?.fields || [])?.filter((item) => item !== value),
    };

    if (name === SEARCH_BY_KEY && onChange) {
      onChange({ name: 'fields', value: newFields?.fields });
    }

    setFilterParams({ ...filterParams, fields: newFields?.fields });
  };

  const handleSearchChange = (value: string) => {
    setSearchKey(value);
  };

  const handleGetEmployeeList = (params: IQueryParamBase) => {
    setIsLoading(true);

    dispatch(getEmployeeList(params))
      .unwrap()
      .then((res) => {
        const { responses, pagination, totalItems } = res?.data;

        if (!responses) return;

        setEmployeeList(responses);
        setPagination(pagination);
        setTotalItems(totalItems.toString());
      })
      .catch((error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleViewEmployee = (id: string) => {
    if (!id) return;

    switch (currentRole) {
      case RoleEnum.ADMIN:
        navigate(`${adminRouteAbsolute.employees}/${id}`);
        break;

      case RoleEnum.STAFF:
        navigate(`${staffRouteAbsolute.employees}/${id}`);
        break;
    }
  };

  const handleEditEmployee = (id: string) => {
    switch (currentRole) {
      case RoleEnum.ADMIN:
        navigate(adminRouteAbsolute.updateEmployee(id));
        break;

      case RoleEnum.STAFF:
        navigate(staffRouteAbsolute.updateEmployee(id));
        break;
    }
  };

  const handleCloseConfirmDelete = () => {
    setIsOpenConfirm(false);
  };

  const handleDeleteEmployee = (id: string) => {
    if (!id) return;

    setDataIds([id]);
    setIsOpenConfirm(true);
  };

  const handleConfirmDelete = () => {
    setIsLoading(true);

    const params: IQueryDeleteEmployee = {
      deleteIds: dataIds,
    };

    dispatch(deleteEmployee(params))
      .unwrap()
      .then(() => {
        setDataIds([]);
        handleCloseConfirmDelete();
        dispatch(employeeActions.setRefreshEmployeeList(true));
      })
      .catch(() => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handlePageChange = (event: ChangeEvent<unknown>, page: number) => {
    setParamObject({ ...paramObject, page });
    setSearchParams({ ...params, page: String(page), limit: String(limitPage) });
  };

  // const handleRowsSelect = (ids: string[]) => {
  //   // TODO: Handle logic row select
  //   setIdsEmployee(ids);
  // };

  const handleRowPerPageChange = (rowPerPage: number) => {
    setParamObject({ ...paramObject, page: BASE_PAGINATION_DEFAULT_CURRENT_PAGE, limit: rowPerPage });
    setSearchParams({
      ...params,
      page: String(BASE_PAGINATION_DEFAULT_CURRENT_PAGE),
      limit: String(rowPerPage),
    });
  };

  const handleCloseUnderDevelopment = () => {
    setIsShowUnderDevelopment(false);
  };

  // const handleDeleteMultiple = () => {
  //   // Handle Delete Multiple
  //   setIsShowUnderDevelopment(true);
  // };

  const handleCloseEmployeeDetail = () => {
    setIsOpenEmployeeDetail(false);
    setEmployeeSelected(EMPTY_STRING);
  };

  const handleFileEmployeeChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (!file.name.endsWith(AcceptFileEnum.XLSX) && !file.name.endsWith(AcceptFileEnum.XLS)) {
        customToast(ToastTypeEnum.WARNING, t('common_warning_excel_file_import', { ns: 'common/base' }));
        return;
      }

      setEmployeeFileSelected(file);
    }
  };

  const handleMakeStatus = (employee: IEmployee) => {
    setEmployee(employee);
    setIsShowConfirmMakeStatus(true);
  };

  const handleCloseConfirmMakeStatus = () => {
    setIsShowConfirmMakeStatus(false);
    setEmployeeSelected(undefined);
  };

  const handleConfirmMakeStatus = () => {
    if (!employee || !employee?.id) return;

    setIsLoading(true);

    dispatch(
      updateStatusEmployee({
        employeeId: employee?.id,
        body: {
          status:
            employee?.status === EmployeeStatusEnum.ACTIVE
              ? EmployeeStatusEnum.INACTIVE
              : EmployeeStatusEnum.ACTIVE,
        },
      })
    )
      .unwrap()
      .then((res) => {
        dispatch(employeeActions.setRefreshEmployeeList(true));
      })
      .catch((error) => {})
      .finally(() => {
        handleCloseConfirmMakeStatus();
        setIsLoading(false);
      });
  };

  const handleFilterEmployeeChange = (
    status: IBaseOption,
    onChange?: (value: IFilterValueChange<IQueryParamBase>) => void
  ) => {
    if (!status) return;

    const { value } = status;

    onChange?.({ name: 'status', value: value });
    setFilterParams({ ...filterParams, status: value });
  };

  const handleNavigateMessage = (employeeId: string) => {
    if (!employeeId) return;

    const payload: IQueryCreateNewConversation = {
      accountIds: [Number(employeeId)],
    };

    setIsLoading(true);

    dispatch(createNewConversation({ accountIds: payload?.accountIds }))
      .unwrap()
      .then((res) => {
        if (!res?.data) return;

        const { id } = res.data;

        if (id) {
          const params = new URLSearchParams({ conversationId: id });

          switch (currentRole) {
            case RoleEnum.ADMIN:
              navigate(`${adminRouteAbsolute.message}?${params.toString()}`);
              break;

            case RoleEnum.STAFF:
              navigate(`${staffRouteAbsolute.message}?${params.toString()}`);
              break;

            default:
              break;
          }
        }
      })
      .catch((error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleOpenSelectFile = () => {
    fileEmployeeRef?.current?.click();
  };
  //#endregion Handle Function

  return (
    <PageShell title={t('menu_employees', { ns: 'common/sidebar' })}>
    <div id='employeePage' className={cx('container')}>
      <BaseToolbar
        title={t('tile_quantity_employee_page', { quantity: totalItems })}
        // onDelete={handleDeleteMultiple}
        // selectedCount={idsEmployee.length}
        // onDelete={handleDeleteMultiple}
        // selectedCount={idsEmployee.length}
      >
        <BaseInputSearch
          searchByContent={
            searchPlaceholder
              ? `${SearchByFilterContentEnum.NAME}, ${searchPlaceholder}`
              : SearchByFilterContentEnum.NAME
          }
          value={searchKey}
          onChange={handleSearchChange}
        />

        <BaseFilters value={filterParams} defaultValue={FILTER_DEFAULT_VALUES} onApply={handleApplyFilter}>
          {({ value, onChange }) => (
            <Stack gap={'12px'}>
              <Typography className={cx('title')}>
                {t('common_action_search_by', { ns: 'common/base' })}
              </Typography>

              <FormGroup>
                <FormControlLabel
                  className={cx('formControlLabel')}
                  slotProps={{
                    typography: {
                      className: cx('checkboxLabel'),
                    },
                  }}
                  control={
                    <Checkbox
                      name={SEARCH_BY_KEY}
                      className={cx('checkbox')}
                      value={SearchByFilterEnum.EMAIL}
                      checked={value?.fields?.includes(SearchByFilterEnum.EMAIL) || false}
                      onChange={(event) => onFilterChildrenChange(event, value, onChange)}
                    />
                  }
                  label={t('filter_label_email')}
                />

                <FormControlLabel
                  className={cx('formControlLabel')}
                  slotProps={{
                    typography: {
                      className: cx('checkboxLabel'),
                    },
                  }}
                  control={
                    <Checkbox
                      name={SEARCH_BY_KEY}
                      className={cx('checkbox')}
                      value={SearchByFilterEnum.PHONE}
                      checked={value?.fields?.includes(SearchByFilterEnum.PHONE) || false}
                      onChange={(event) => onFilterChildrenChange(event, value, onChange)}
                    />
                  }
                  label={t('filter_label_phone')}
                />

                <FormControlLabel
                  className={cx('formControlLabel')}
                  slotProps={{
                    typography: {
                      className: cx('checkboxLabel'),
                    },
                  }}
                  control={
                    <Checkbox
                      name={SEARCH_BY_KEY}
                      className={cx('checkbox')}
                      value={SearchByFilterEnum.ROLE}
                      checked={value?.fields?.includes(SearchByFilterEnum.ROLE) || false}
                      onChange={(event) => onFilterChildrenChange(event, value, onChange)}
                    />
                  }
                  label={t('filter_label_position')}
                />
              </FormGroup>

              <Typography className={cx('title')}>
                {t('common_action_filter_by', { ns: 'common/base' })}
              </Typography>

              <Box component={'div'} className={cx('filterGroup')}>
                <InputLabel className={cx('label')}>{t('filter_label_status')}</InputLabel>

                <BaseSelect
                  layout='horizontal'
                  // label={t('filter_label_status')}
                  width={'135px'}
                  options={mockDataEmployeeStatus}
                  value={value?.status || EmployeeStatusEnum.ALL}
                  onChange={(option) => handleFilterEmployeeChange(option, onChange)}
                />
              </Box>
            </Stack>
          )}
        </BaseFilters>

        <BaseSplitButton label={t('button_add_employee')} onClick={handleNavigateToFormEmployee}>
          <Button variant='text' fullWidth onClick={handleOpenSelectFile}>
            {t('button_upload_employee')}
          </Button>
        </BaseSplitButton>

        <input
          type='file'
          className={cx('fileUpload')}
          ref={fileEmployeeRef}
          accept={AcceptFileEnum.EXCEL}
          onChange={handleFileEmployeeChange}
        />
      </BaseToolbar>

      <Box component={'div'} className={cx('body')}>
        <BaseTable
          currentRowsPerPage={limitPage}
          currentPage={pageSelected}
          count={pagination?.totalPages || DEFAULT_NUMBER_ZERO}
          columns={columns(
            t,
            handleViewEmployee,
            handleEditEmployee,
            handleDeleteEmployee,
            handleMakeStatus,
            handleNavigateMessage
          )}
          dataSource={employeeList || []}
          // dataSelectedIds={idsEmployee}
          onPageChange={handlePageChange}
          // onSelectChange={handleRowsSelect}
          onRowClick={handleViewEmployee}
          onRowsPerPageChange={handleRowPerPageChange}
        />
      </Box>

      {isLoading && <Loading />}

      {isOpenConfirm && (
        <ConfirmModal
          type='delete'
          icon={icons.tableIconActionDelete}
          title={t('common_title_confirm_delete', { ns: 'common/base' })}
          textConfirm={t('common_action_delete', { ns: 'common/base' })}
          onClose={handleCloseConfirmDelete}
          onConfirm={handleConfirmDelete}
        />
      )}

      {isShowConfirmMakeStatus && (
        <ConfirmModal
          type={employee?.status === EmployeeStatusEnum.ACTIVE ? 'delete' : 'default'}
          icon={
            employee?.status === EmployeeStatusEnum.ACTIVE
              ? icons.tableIconActionMakeStatus
              : icons.commonIconCheckMark
          }
          title={t(
            employee?.status === EmployeeStatusEnum.ACTIVE
              ? 'common_title_confirm_make_status_deactivate'
              : 'common_title_confirm_make_status_activate',
            { ns: 'common/base' }
          )}
          description={t(
            employee?.status === EmployeeStatusEnum.ACTIVE
              ? 'common_desc_confirm_make_status_deactivate'
              : 'common_desc_confirm_make_status_activate',
            { ns: 'common/base' }
          )}
          textConfirm={t(
            employee?.status === EmployeeStatusEnum.ACTIVE
              ? 'common_action_deactivate'
              : 'common_action_activate',
            { ns: 'common/base' }
          )}
          onClose={handleCloseConfirmMakeStatus}
          onConfirm={handleConfirmMakeStatus}
        />
      )}

      {/* <EmployeeDetail
        isOpen={isOpenEmployeeDetail}
        onClose={handleCloseEmployeeDetail}
        employeeId={employeeSelected as string}
      /> */}

      <EmployeeDetailModal
        isOpen={isOpenEmployeeDetail}
        onClose={handleCloseEmployeeDetail}
        employeeId={employeeSelected as string}
      />

      <UnderDevelopment isOpen={isShowUnderDevelopment} onClose={handleCloseUnderDevelopment} />
    </div>
    </PageShell>
  );
};

export default Employee;
